import React from "react";
import { alpha } from "@mui/material/styles";
import { MODULE_BRAND_HEX } from "../constants/moduleBrand";

/**
 * Shared modal transition with no layout reads.
 * MUI Slide/Fade can race on fast mount/unmount and read null nodes.
 */
export const EntityModalTransition = React.forwardRef(
  function EntityModalTransition(props, ref) {
    const { children, in: inProp, onEnter, onEntered, onExit, onExited } = props;
    const wasOpenRef = React.useRef(Boolean(inProp));

    React.useEffect(() => {
      const wasOpen = wasOpenRef.current;
      const isOpen = Boolean(inProp);

      if (!wasOpen && isOpen) {
        onEnter?.(null, false);
        onEntered?.(null, false);
      } else if (wasOpen && !isOpen) {
        onExit?.(null);
        onExited?.(null);
      }

      wasOpenRef.current = isOpen;
    }, [inProp, onEnter, onEntered, onExit, onExited]);

    if (!inProp) return null;
    return React.cloneElement(children, { ref });
  },
);

function moduleBrandHex(moduleKey) {
  if (!moduleKey || moduleKey === "Core") return null;
  return MODULE_BRAND_HEX[moduleKey] || null;
}

/**
 * Paper styling for module dialogs: glassy surface + subtle module-tint wash.
 * Pass `moduleKey` (Accounting, HR, Marketing, …) for brand tint; Core uses primary.
 */
export function getUnifiedModalPaperProps(moduleKey) {
  return {
    sx: (theme) => {
      const isDark = theme.palette.mode === "dark";
      const hex = moduleBrandHex(moduleKey);
      const tint = hex || theme.palette.primary.main;
      const basePaper = theme.palette.background.paper;
      const wash = alpha(tint, isDark ? 0.22 : 0.12);
      const edge = alpha(
        theme.palette.divider,
        isDark ? 0.45 : 0.7,
      );
      return {
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${edge}`,
        boxShadow: theme.shadows[isDark ? 16 : 10],
        background: [
          `linear-gradient(165deg, ${alpha(basePaper, isDark ? 0.78 : 0.98)} 0%, ${alpha(basePaper, isDark ? 0.55 : 0.94)} 42%, ${alpha(theme.palette.background.default, isDark ? 0.72 : 0.99)} 100%)`,
          `radial-gradient(110% 70% at 0% 0%, ${wash}, transparent 52%)`,
        ].join(", "),
        backdropFilter: "blur(20px)",
      };
    },
  };
}

/** @deprecated Prefer {@link getUnifiedModalPaperProps} with a real `moduleKey`. */
export const entityModalPaperProps = getUnifiedModalPaperProps("Core");

/**
 * Dialog title strip: module brand gradient (same language as ModuleHeroHeader).
 * Returns an `sx` callback so it works with MUI `theme`.
 */
export function getUnifiedModalTitleSx(moduleKey) {
  return (theme) => {
    const isDark = theme.palette.mode === "dark";
    const hex = moduleBrandHex(moduleKey);
    const deep = "#0f172a";
    const bg = hex
      ? `linear-gradient(135deg, ${hex} 0%, ${deep} 100%)`
      : `linear-gradient(125deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${alpha(theme.palette.primary.light, 0.92)} 110%)`;
    return {
      px: 3,
      py: 2.25,
      m: 0,
      typography: "h6",
      fontWeight: 800,
      letterSpacing: -0.02,
      color: "#f8fafc",
      background: bg,
      borderBottom: `1px solid ${alpha("#ffffff", isDark ? 0.1 : 0.14)}`,
      boxShadow: `inset 0 -1px 0 ${alpha("#000", 0.15)}`,
      /** Title case for plain title text; nested Typography resets below. */
      textTransform: "capitalize",
      "& .MuiTypography-root": {
        textTransform: "none",
      },
    };
  };
}

/** Merge module title bar with extra `sx` (e.g. flex layout for toolbar in title). */
export function mergeUnifiedModalTitleSx(moduleKey, extraSx = {}) {
  return (theme) => ({
    ...getUnifiedModalTitleSx(moduleKey)(theme),
    ...(typeof extraSx === "function" ? extraSx(theme) : extraSx),
  });
}

/** Footer row: aligned actions, subtle top border. */
export const unifiedModalDialogActionsSx = (theme) => ({
  px: 3,
  py: 2,
  gap: 1.5,
  justifyContent: "flex-end",
  flexWrap: "wrap",
  borderTop: `1px solid ${theme.palette.divider}`,
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.black, 0.2)
      : alpha(theme.palette.grey[50], 0.95),
});

/** @deprecated Use {@link unifiedModalDialogActionsSx} (same shape). */
export const entityModalDialogActionsSx = unifiedModalDialogActionsSx;

/** Primary action on module modals (Save / Submit). */
export const entityModalSubmitButtonProps = {
  variant: "contained",
  color: "secondary",
  sx: { textTransform: "uppercase", fontWeight: 700 },
};

/** Cancel / dismiss on module modals. */
export const entityModalCancelButtonProps = {
  variant: "contained",
  color: "error",
};

/** TextField labels + outline use theme secondary when focused. */
export const formFieldSecondaryFocusSx = {
  "& .MuiInputLabel-root": { color: "secondary.main" },
  "& .MuiInputLabel-root.Mui-focused": { color: "secondary.main" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.main",
  },
};

/** Optional: subtle body area behind form fields inside `DialogContent`. */
export function getUnifiedModalDialogContentSx(extra = {}) {
  return (theme) => ({
    ...extra,
    bgcolor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.black, 0.12)
        : alpha(theme.palette.grey[50], 0.65),
    backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.35)} 0%, transparent 48%)`,
  });
}
